import { Component, OnInit } from '@angular/core';
import { Notification } from '../interfaces/notification.interface';
import { trackById } from '@vex/utils/track-by';
import { VexDateFormatRelativePipe } from '@vex/pipes/vex-date-format-relative/vex-date-format-relative.pipe';
import { RouterLink } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { CommonModule, NgClass, NgFor } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { LiveNotificationService } from 'src/app/services/live-notification.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MailComponent } from 'src/app/pages/mail/containers/mail.component';
import { MailService } from 'src/app/pages/mail/services/mail.service';
import { of } from 'rxjs';

@Component({
    selector: 'vex-toolbar-notifications-dropdown',
    templateUrl: './toolbar-notifications-dropdown.component.html',
    styleUrls: ['./toolbar-notifications-dropdown.component.scss'],
    imports: [
        CommonModule,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgFor,
        MatRippleModule,
        RouterLink,
        NgClass,
        VexDateFormatRelativePipe
    ]
})
export class ToolbarNotificationsDropdownComponent implements OnInit {
  notifications: Notification[] = [];

  trackById = trackById;

  constructor(
    private liveNotification: LiveNotificationService,
    private router: Router,
    private dialog: MatDialog,
    private mailService: MailService
  ) {}

  ngOnInit() {
    this.liveNotification.notifications$.subscribe((list) => {
      this.notifications = list;
    });
  }

  markAllAsRead(): void {
    this.liveNotification.markAllAsRead();
  }

  openNotification(item: Notification): void {
    this.liveNotification.markAsRead(item.id);
    const payload = item?.payload?.payload ?? item?.payload ?? {};
    const mailId = Number(payload?.mailId ?? payload?.mail_id ?? 0);
    const configId = Number(payload?.configId ?? payload?.config_id ?? 0);

    if (mailId > 0) {
      this.openMailNotification(configId, mailId);
      return;
    }

    const checklistExecutionId = Number(
      payload?.checklist_execution_id ?? payload?.checklistExecutionId ?? 0
    );

    if (checklistExecutionId > 0) {
      this.router.navigate(['/qac/checklistexec'], {
        queryParams: { checklist_execution_id: checklistExecutionId }
      });
      return;
    }

    if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  private openMailNotification(configId: number, mailId: number): void {
    const continueOpenMail = () => {
      this.ensureMailDialogOpen();

      const accounts$ = this.mailService.accounts.value.length > 0
        ? of(this.mailService.accounts.value)
        : this.mailService.loadAccounts();

      accounts$.subscribe({
        next: (accounts) => {
          const hasRequestedAccount = accounts.some((account) => Number(account.id) === configId);
          const accountIdToUse = hasRequestedAccount
            ? configId
            : Number(accounts[0]?.id || this.mailService.selectedAccountId.value || 0);

          if (accountIdToUse > 0) {
            this.mailService.selectAccount(accountIdToUse);
          } else {
            this.mailService.selectFolder('INBOX');
            this.mailService.loadMessages(1);
          }

          window.setTimeout(() => {
            this.mailService.selectMail(mailId);
          }, 250);
        },
        error: () => {
          this.ensureMailDialogOpen();
          if (configId > 0) {
            this.mailService.selectAccount(configId);
          }
          window.setTimeout(() => {
            this.mailService.selectMail(mailId);
          }, 250);
        }
      });
    };

    if (this.mailService.hasMailAccess()) {
      continueOpenMail();
      return;
    }

    this.mailService.loadAccess().subscribe({
      next: (access) => {
        if (!access?.is_mail_enabled) {
          return;
        }
        continueOpenMail();
      }
    });
  }

  private ensureMailDialogOpen(): void {
    const existingMailDialog = this.dialog.openDialogs.find(
      (dialogRef) => dialogRef.componentInstance instanceof MailComponent
    );

    if (existingMailDialog) {
      return;
    }

    const dialogconfig = new MatDialogConfig();
    dialogconfig.autoFocus = true;
    dialogconfig.disableClose = false;
    dialogconfig.maxHeight = '100vh';
    dialogconfig.maxWidth = '100vw';
    dialogconfig.height = '100%';
    dialogconfig.width = '100%';
    dialogconfig.panelClass = 'custom-dialog-container';
    dialogconfig.hasBackdrop = true;

    this.dialog.open(MailComponent, dialogconfig);
  }
}
